import React from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  TooltipProps
} from 'recharts';
import { Box, Card, CardContent, CardHeader, Typography, CircularProgress, useTheme } from '@mui/material';
import { ChartData } from 'src/types/analytics';

interface ConversationStatusChartProps {
  title: string;
  data: ChartData;
  height?: number;
  loading?: boolean;
}

// Type definition for the custom tooltip props
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: any }>;
}

// Type definition for pie label render props
interface PieLabelRenderProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
  value: number;
  name: string;
}

const ConversationStatusChart: React.FC<ConversationStatusChartProps> = ({
  title,
  data,
  height = 300,
  loading = false
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardHeader title={title} />
        <CardContent sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.labels || !data.datasets || data.labels.length === 0) {
    return (
      <Card>
        <CardHeader title={title} />
        <CardContent sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Create custom data for the pie chart that uses legendLabels for the Legend
  const pieData = data.datasets[0].data.map((value, index) => ({
    name: data.labels[index],
    legendName: data.legendLabels?.[index] || data.labels[index],
    value,
    color: Array.isArray(data.datasets[0].backgroundColor)
      ? data.datasets[0].backgroundColor[index]
      : '#000'
  }));

  // Calculate total for percentages
  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  // Custom render for the Pie chart label to avoid overlapping
  const renderCustomizedLabel = (props: PieLabelRenderProps) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, value, name } = props;

    // Only show percentage labels for segments with significant percentage
    if (percent < 0.08) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // For large segments, display inside the segment
    const percentLabel = `${(percent * 100).toFixed(0)}%`;

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="bold"
        fontSize={12}
      >
        {percentLabel}
      </text>
    );
  };

  // Custom tooltip to display both status name and percentage
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{
          bgcolor: 'background.paper',
          p: 1.5,
          border: '1px solid #ccc',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Typography variant="body2" fontWeight="medium">{data.name}</Typography>
          <Typography variant="body2" fontWeight="bold">
            {`${data.value} (${((data.value / total) * 100).toFixed(1)}%)`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <Box sx={{ height: height - 75, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={75}
                innerRadius={50}
                paddingAngle={2}
                dataKey="value"
                labelLine={false}
                label={renderCustomizedLabel}
                animationDuration={750}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke={theme.palette.background.paper}
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend */}
        <Box sx={{
          mt: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1.5
        }}>
          {pieData.map((entry, index) => (
            <Box
              key={`legend-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  bgcolor: entry.color,
                  mr: 1,
                  borderRadius: '50%'
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: '0.75rem' }}
              >
                {entry.legendName || entry.name}: {entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConversationStatusChart; 